<template>
  <nav class="navbar">
    <router-link to="/" class="logo">
      <span class="logo-light">Code</span>
      <span class="logo-medium">Ship</span>
      <span class="logo-dark">Watch</span>
    </router-link>
    <div class="nav-links">
      <router-link v-if="isAdmin" to="/admin" class="admin-link">Admin</router-link>
      <button v-if="isAuthenticated" class="logout-btn" @click="handleLogout">
        Logout
      </button>
    </div>
  </nav>
</template>

<script>
import { Auth } from '@aws-amplify/auth'
import { ref, onMounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import { checkAdminStatus, clearAdminCache } from '../services/adminAuth'

export default {
  name: 'NavigationBar',
  setup() {
    const isAuthenticated = ref(false)
    const isAdmin = ref(false)
    const router = useRouter()
    const logger = inject('logger')

    const checkAuthState = async () => {
      try {
        await Auth.currentAuthenticatedUser()
        isAuthenticated.value = true
        logger.debug('User is authenticated')
        
        // Check if user is admin
        const adminStatus = await checkAdminStatus()
        isAdmin.value = adminStatus
        logger.debug(`User admin status: ${adminStatus}`)
      } catch {
        isAuthenticated.value = false
        isAdmin.value = false
        logger.info('User is not authenticated, redirecting to login')
        router.push('/login')
      }
    }

    const handleLogout = async () => {
      try {
        await Auth.signOut()
        isAuthenticated.value = false
        isAdmin.value = false
        clearAdminCache() // Clear admin status cache
        logger.info('User logged out successfully')
        router.push('/login')
      } catch (error) {
        logger.error('Error signing out:', { error: error.message })
      }
    }

    onMounted(() => {
      logger.debug('NavigationBar component mounted')
      checkAuthState()
    })

    return {
      isAuthenticated,
      isAdmin,
      handleLogout
    }
  }
}
</script>

<style scoped>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  background-color: #ffffff;
  color: var(--primary);
  padding: 0 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
}

.logo {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.5px;
  display: flex;
  gap: 0.1rem;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.logo:hover {
  opacity: 0.8;
}

.logo-light {
  color: #90cdf4; /* Lightest blue */
}

.logo-medium {
  color: #4299e1; /* Medium blue */
}

.logo-dark {
  color: #2b6cb0; /* Darker blue */
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.logout-btn {
  background-color: #f56565; /* Red color for logout */
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.logout-btn:hover {
  background-color: #e53e3e; /* Darker red on hover */
}

.admin-link {
  margin-right: 1rem;
  text-decoration: none;
  color: var(--primary);
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.admin-link:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
